import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RetailCreatorChatCatalogItem,
  creatorChatRetailService,
} from '../services/creatorChatService';

function formatPlatformLabel(platform?: string | null): string {
  if (!platform) return 'Creator';
  switch (platform.toUpperCase()) {
    case 'FANVUE':
      return 'Fanvue';
    case 'ONLYFANS':
      return 'OnlyFans';
    case 'BOTH':
      return 'Fanvue & OnlyFans';
    default:
      return platform;
  }
}

function formatUpdatedAt(value?: string | null): string {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

const RetailCreatorChatCatalog: React.FC = () => {
  const navigate = useNavigate();
  const [bots, setBots] = useState<RetailCreatorChatCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await creatorChatRetailService.listPublishedModals();
        if (!cancelled) setBots(rows);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load AI creators');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f4f4', padding: '24px 16px 48px' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ marginBottom: 24 }}>
          <h1
            style={{
              fontFamily: 'Nunito, sans-serif',
              fontWeight: 800,
              fontSize: 28,
              color: '#0f172a',
              margin: 0,
            }}
          >
            AI Creator Chat
          </h1>
          <p
            style={{
              fontFamily: 'Mukta, sans-serif',
              fontSize: 15,
              color: '#64748b',
              marginTop: 8,
              marginBottom: 0,
            }}
          >
            Chat with published creator personalities — voice, video actions, and memory when enabled.
          </p>
        </div>

        {error ? (
          <div
            role="alert"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              padding: 16,
              background: 'rgba(255, 113, 113, 0.1)',
              border: '1px solid #ff7171',
              borderRadius: 12,
              marginBottom: 24,
            }}
          >
            <p style={{ fontFamily: 'Nunito, sans-serif', color: '#ff7171', fontSize: 14, margin: 0 }}>
              {error}
            </p>
          </div>
        ) : null}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <svg
              className="animate-spin"
              style={{ width: 32, height: 32, color: '#94a3b8' }}
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                style={{ opacity: 0.25 }}
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                style={{ opacity: 0.75 }}
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        ) : null}

        {!loading && !error && bots.length === 0 ? (
          <div
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 12,
              padding: 48,
              textAlign: 'center',
              boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
            }}
          >
            <h3
              style={{
                fontFamily: 'Nunito, sans-serif',
                fontWeight: 700,
                fontSize: 16,
                color: '#0f172a',
                margin: '0 0 8px',
              }}
            >
              No creators available yet
            </h3>
            <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: 14, color: '#64748b', margin: 0 }}>
              Published AI creator chats will appear here when your administrator enables them.
            </p>
          </div>
        ) : null}

        {!loading && !error && bots.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 20,
            }}
          >
            {bots.map((bot) => {
              const accent = bot.accentColor || '#086afb';
              const title = bot.title || bot.creatorLabel || 'Creator';
              return (
                <button
                  key={bot.publicId}
                  type="button"
                  onClick={() => navigate(`/creator-chat/${encodeURIComponent(bot.publicId)}`)}
                  style={{
                    textAlign: 'left',
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 16,
                    padding: 0,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(15,23,42,0.06)',
                    transition: 'box-shadow 0.2s, transform 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(15,23,42,0.12)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(15,23,42,0.06)';
                    e.currentTarget.style.transform = 'none';
                  }}
                >
                  <div
                    style={{
                      height: 140,
                      background: `linear-gradient(135deg, ${accent}22 0%, ${accent}44 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                    }}
                  >
                    {bot.avatarImageUrl ? (
                      <img
                        src={bot.avatarImageUrl}
                        alt=""
                        style={{
                          width: 88,
                          height: 88,
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: `3px solid ${accent}`,
                          boxShadow: '0 4px 16px rgba(15,23,42,0.15)',
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 88,
                          height: 88,
                          borderRadius: '50%',
                          background: accent,
                          opacity: 0.35,
                        }}
                      />
                    )}
                  </div>
                  <div style={{ padding: '16px 18px 18px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontFamily: 'Nunito, sans-serif',
                            fontWeight: 800,
                            fontSize: 17,
                            color: '#0f172a',
                            lineHeight: 1.25,
                          }}
                        >
                          {title}
                        </div>
                        {bot.subtitle ? (
                          <div
                            style={{
                              fontFamily: 'Mukta, sans-serif',
                              fontSize: 13,
                              color: '#64748b',
                              marginTop: 4,
                            }}
                          >
                            {bot.subtitle}
                          </div>
                        ) : null}
                      </div>
                      <span
                        style={{
                          flexShrink: 0,
                          padding: '4px 10px',
                          borderRadius: 999,
                          background: `${accent}18`,
                          color: accent,
                          fontFamily: 'Mukta, sans-serif',
                          fontSize: 11,
                          fontWeight: 600,
                        }}
                      >
                        {formatPlatformLabel(bot.targetPlatform)}
                      </span>
                    </div>

                    {bot.welcomeMessage ? (
                      <p
                        style={{
                          fontFamily: 'Nunito, sans-serif',
                          fontSize: 13,
                          color: '#475569',
                          margin: '12px 0 0',
                          lineHeight: 1.45,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {bot.welcomeMessage}
                      </p>
                    ) : null}

                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 6,
                        marginTop: 14,
                      }}
                    >
                      {bot.voiceEnabled ? (
                        <span
                          style={{
                            padding: '3px 10px',
                            borderRadius: 999,
                            background: 'rgba(13, 202, 157, 0.1)',
                            color: '#0dca9d',
                            fontFamily: 'Mukta, sans-serif',
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          Voice
                        </span>
                      ) : null}
                      {bot.actionsEnabled ? (
                        <span
                          style={{
                            padding: '3px 10px',
                            borderRadius: 999,
                            background: 'rgba(8, 106, 251, 0.1)',
                            color: '#086afb',
                            fontFamily: 'Mukta, sans-serif',
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          Video actions
                        </span>
                      ) : null}
                      {formatUpdatedAt(bot.updatedAt) ? (
                        <span
                          style={{
                            padding: '3px 10px',
                            borderRadius: 999,
                            background: '#f1f5f9',
                            color: '#94a3b8',
                            fontFamily: 'Mukta, sans-serif',
                            fontSize: 11,
                          }}
                        >
                          Updated {formatUpdatedAt(bot.updatedAt)}
                        </span>
                      ) : null}
                    </div>

                    <div
                      style={{
                        marginTop: 16,
                        padding: '10px 0 0',
                        borderTop: '1px solid #f1f5f9',
                        fontFamily: 'Mukta, sans-serif',
                        fontSize: 14,
                        fontWeight: 600,
                        color: accent,
                      }}
                    >
                      Open chat →
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default RetailCreatorChatCatalog;
